<template>
  <container-slot>
    <v-col cols="12">
      <h1 class="display-1 font-weight-medium">Edit User</h1>
    </v-col>
    <v-col cols="12" v-show="successSubmit">
      <v-alert
        :type="!error ? `success` : info ? `info` : `error`"
        ref="alert"
        dismissible
        @input="close"
        class="noMarg"
        v-show="successSubmit"
      >
        {{ msg }}
      </v-alert>
    </v-col>
    <v-col>
      <v-form ref="form" v-model="valid" @submit="submit">
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              prepend-inner-icon="mdi-account"
              name="username"
              label="Username"
              type="text"
              v-model="userName"
              :rules="userNameRules"
              ref="userNameInput"
              disabled
              outlined
              required
            >
            </v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              :disabled="userSameAsCurrent(userId)"
              v-model="selectedRole"
              :items="selectRoleData"
              menu-props="auto"
              label="Role"
              prepend-inner-icon="mdi-pencil"
              ref="inputRole"
              :rules="selectRoleRules"
              single-line
              outlined
              required
            ></v-select>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              prepend-inner-icon="mdi-account"
              name="fullname"
              label="Fullname"
              type="text"
              v-model="fullName"
              :rules="fullNameRules"
              ref="fullNameInput"
              outlined
            >
            </v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              prepend-inner-icon="mdi-email"
              name="email"
              label="Email"
              type="email"
              v-model="email"
              :rules="emailRules"
              ref="emailInput"
              outlined
            >
            </v-text-field>
          </v-col>
        </v-row>
        <v-btn
          :disabled="!valid"
          color="primary"
          @click="submit"
          ref="submitBtn"
          :loading="loading"
        >
          Submit
        </v-btn>
      </v-form>
    </v-col>
  </container-slot>
</template>

<script>
import ContainerSlot from "@/components/ContainerSlot";
import { lsUtils } from "@/utils/localStorageUtils";
import { getUserById, roles, users } from "@/utils/graphqlQuery";
import { editUser } from "@/utils/graphqlMutation";

export default {
  apollo: {
    selectRoleData: {
      query: roles,
      update: function (data) {
        const { roles } = data;
        const newRoles = roles.map((role) => ({
          text: role.roleName,
          value: role.id,
        }));
        newRoles.push({ text: "Select a Role", value: -1 });
        newRoles.sort((a, b) => a.value - b.value);
        return newRoles;
      },
    },
    userData: {
      query: getUserById,
      update: function (data) {
        if (!data.getUserById) {
          this.$router.replace({ path: "/user/edit" });
        } else {
          const { userName, fullName, email } = data.getUserById;
          const role = data.getUserById.role;
          this.userName = userName;
          this.selectedRole = role ? role.id : -1;
          this.fullName = fullName;
          this.email = email;
          return data.getUserById;
        }
      },
      variables() {
        return {
          userId: this.userId,
        };
      },
    },
  },
  data() {
    return {
      error: false,
      info: false,
      msg: "",
      loading: false,
      selectedRole: -1,
      selectRoleData: [{ text: "Select a Role", value: -1 }],
      selectRoleRules: [(v) => v > -1 || "Role must be selected"],
      userName: "",
      userNameRules: [
        (v) => !!v || "Username is required",
        (v) => /^[A-Za-z0-9-_.]{1,}$/.test(v) || "Username must be valid",
      ],
      fullName: "",
      fullNameRules: [
        (v) => !v || /^[A-Za-z ]{1,}$/.test(v) || "Fullname must be valid",
      ],
      email: "",
      emailRules: [
        (v) =>
          !v ||
          /^[\w.-]+@(?:[A-Za-z-]+\.)+[A-Za-z]{2,}$/.test(v) ||
          "Email must be valid",
      ],
      successSubmit: false,
      userId: this.$route.params.userId,
      valid: false,
    };
  },
  components: {
    ContainerSlot,
  },
  methods: {
    async submit(e) {
      e.preventDefault();
      this.successSubmit = false;
      const that = this;
      if (this.valid) {
        this.loading = true;
        const gqlUsers = users;
        try {
          const userObj = {
            id: this.userId,
            userName: this.userName,
            roleId: this.selectedRole,
            isActive: 1,
            fullName: this.fullName || null,
            email: this.email || null,
          };
          const { data } = await this.$apollo.mutate({
            mutation: editUser,
            variables: {
              userObj,
            },
            update: (store, { data: { editUser } }) => {
              try {
                if (editUser.success == 1) {
                  const cachedUsers = store.readQuery({ query: gqlUsers });
                  const mappedCache = cachedUsers.users.map((u) => {
                    if (u.id == userObj.id) {
                      let userObjWithoutRoleId = { ...userObj };
                      delete userObjWithoutRoleId.roleId;

                      let getCurrentUserRoleData = that.selectRoleData.filter(
                        (r) => r.value == that.selectedRole
                      );
                      let newRoleData = {
                        ...that.userData.role,
                        id: getCurrentUserRoleData[0].value,
                        roleName: getCurrentUserRoleData[0].text,
                      };

                      let modifiedUser = {
                        ...that.userData,
                        ...userObjWithoutRoleId,
                        role: newRoleData,
                      };
                      return modifiedUser;
                    }
                    return u;
                  });
                  store.writeQuery({
                    query: gqlUsers,
                    data: {
                      users: mappedCache,
                    },
                  });
                  return mappedCache;
                }
              } catch (err) {
                // eslint-disable-next-line no-console
                console.log('No "users" cache found');
                return null;
              }
            },
          });

          if (data.editUser) {
            const { success, msg } = data.editUser;
            if (success == 0) {
              throw new Error(msg);
            } else if (success == -1) {
              this.error = true;
              this.info = true;
              this.msg = msg;
            } else {
              this.error = false;
              this.msg = msg;
            }
          } else {
            this.error = true;
            this.msg = `Something went wrong`;
          }
        } catch (error) {
          this.error = true;
          this.msg = error.message;
        } finally {
          this.successSubmit = true;
          this.loading = false;
        }
      }
    },
    reset() {
      this.$refs.form.reset();
    },
    close() {
      this.successSubmit = false;
      this.error = false;
      this.msg = "";
      this.loading = false;
    },
    userSameAsCurrent(id) {
      const userId = lsUtils.getItem("userToken").userData.userId;
      return id == userId;
    },
  },
  name: "EditUser",
  title() {
    return "Edit User";
  },
};
</script>

<style scoped>
.noMarg {
  margin: 0;
}
</style>
